import { useState } from "react";
import {
  Settings2,
  AlertTriangle,
  Zap,
  Users,
  DollarSign,
  Mail,
  Shield,
  Trash2,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ConfirmModal } from "@/components/admin/ConfirmModal";
import {
  getPlatformSettings,
  getFeatureFlags,
  getAdminUsers,
} from "@/lib/admin/queries";
import {
  updatePlatformSettings,
  updateFeatureFlag,
  updatePricing,
  inviteAdmin,
  revokeAdmin,
  flushAllSessions,
  enableMaintenanceMode,
} from "@/lib/admin/actions";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

// ─── General Tab ──────────────────────────────────────────────────────────────

const generalSchema = z.object({
  platform_name: z.string().min(1, "Required"),
  support_email: z.string().email("Must be a valid email"),
  company_address: z.string().optional(),
  default_night_cap: z.coerce.number().int().min(1).max(365),
  trial_days: z.coerce.number().int().min(1).max(90),
  timezone: z.string().min(1, "Required"),
});

type GeneralForm = z.infer<typeof generalSchema>;

function GeneralTab() {
  const { profile } = useAuth();
  const actorId = profile?.id ?? "";
  const qc = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: ["admin", "platform-settings"],
    queryFn: getPlatformSettings,
  });

  const settings = settingsQuery.data?.data ?? {};

  const form = useForm<GeneralForm>({
    resolver: zodResolver(generalSchema),
    values: {
      platform_name: settings.platform_name ?? "Stratakey",
      support_email: settings.support_email ?? "info@stratakey.ca",
      company_address: settings.company_address ?? "",
      default_night_cap: settings.default_night_cap ?? 90,
      trial_days: settings.trial_days ?? 7,
      timezone: settings.timezone ?? "America/Vancouver",
    },
  });

  const saveMutation = useMutation({
    mutationFn: (data: GeneralForm) => updatePlatformSettings(data, actorId),
    onSuccess: () => {
      toast.success("Settings saved");
      qc.invalidateQueries({ queryKey: ["admin", "platform-settings"] });
    },
    onError: () => toast.error("Failed to save settings"),
  });

  if (settingsQuery.isLoading) {
    return (
      <div className="space-y-4 max-w-lg">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="space-y-1">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-9 w-full" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => saveMutation.mutate(data))}
        className="space-y-5 max-w-lg"
      >
        <FormField
          control={form.control}
          name="platform_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="whitespace-nowrap">Platform name</FormLabel>
              <FormControl><Input {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="support_email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="whitespace-nowrap">Support email</FormLabel>
              <FormControl><Input type="email" {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="company_address"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="whitespace-nowrap">Company address</FormLabel>
              <FormControl><Textarea rows={3} {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="default_night_cap"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Default night cap for new buildings</FormLabel>
              <FormControl><Input type="number" min={1} max={365} {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="trial_days"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Trial period length (days)</FormLabel>
              <FormControl><Input type="number" min={1} max={90} {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="timezone"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="whitespace-nowrap">Platform timezone</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="America/Vancouver">America/Vancouver (PT)</SelectItem>
                  <SelectItem value="America/Edmonton">America/Edmonton (MT)</SelectItem>
                  <SelectItem value="America/Winnipeg">America/Winnipeg (CT)</SelectItem>
                  <SelectItem value="America/Toronto">America/Toronto (ET)</SelectItem>
                  <SelectItem value="America/Halifax">America/Halifax (AT)</SelectItem>
                  <SelectItem value="UTC">UTC</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" className="whitespace-nowrap" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? "Saving…" : "Save Settings"}
        </Button>
      </form>
    </Form>
  );
}

// ─── Pricing Tab ──────────────────────────────────────────────────────────────

const pricingSchema = z.object({
  price_individual: z.coerce.number().min(0),
  price_strata: z.coerce.number().min(0),
  price_property_manager: z.coerce.number().min(0),
  currency_display: z.enum(["CAD", "USD"]),
  gst_hst_enabled: z.boolean(),
});

type PricingForm = z.infer<typeof pricingSchema>;

function PricingTab() {
  const { profile } = useAuth();
  const actorId = profile?.id ?? "";
  const qc = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: ["admin", "platform-settings"],
    queryFn: getPlatformSettings,
  });

  const settings = settingsQuery.data?.data ?? {};

  const form = useForm<PricingForm>({
    resolver: zodResolver(pricingSchema),
    values: {
      price_individual: settings.price_individual ?? 9.99,
      price_strata: settings.price_strata ?? 18.99,
      price_property_manager: settings.price_property_manager ?? 28.99,
      currency_display: settings.currency_display ?? "CAD",
      gst_hst_enabled: settings.gst_hst_enabled ?? false,
    },
  });

  const saveMutation = useMutation({
    mutationFn: (data: PricingForm) => updatePricing(data, actorId),
    onSuccess: () => {
      toast.success("Pricing settings saved");
      qc.invalidateQueries({ queryKey: ["admin", "platform-settings"] });
    },
    onError: () => toast.error("Failed to save pricing"),
  });

  return (
    <div className="space-y-5 max-w-lg">
      <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-4 py-3">
        <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-700">
          <strong>Price changes apply to new subscribers only.</strong> Existing subscribers keep their current price.
        </p>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((data) => saveMutation.mutate(data))}
          className="space-y-5"
        >
          <FormField
            control={form.control}
            name="price_individual"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Individual plan price (CA$)</FormLabel>
                <FormControl>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 whitespace-nowrap">CA$</span>
                    <Input type="number" step="0.01" min="0" className="pl-12" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="price_strata"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Strata Council plan price (CA$)</FormLabel>
                <FormControl>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 whitespace-nowrap">CA$</span>
                    <Input type="number" step="0.01" min="0" className="pl-12" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="price_property_manager"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Property Manager plan price per building (CA$)</FormLabel>
                <FormControl>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 whitespace-nowrap">CA$</span>
                    <Input type="number" step="0.01" min="0" className="pl-12" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="currency_display"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="whitespace-nowrap">Currency display</FormLabel>
                <FormControl>
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    className="flex gap-4 mt-1"
                  >
                    {(["CAD", "USD"] as const).map((v) => (
                      <div key={v} className="flex items-center gap-2">
                        <RadioGroupItem value={v} id={`currency-${v}`} />
                        <Label htmlFor={`currency-${v}`} className="text-sm font-normal cursor-pointer">{v}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="gst_hst_enabled"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-3">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      id="gst-toggle"
                    />
                  </FormControl>
                  <Label htmlFor="gst-toggle" className="text-sm font-normal cursor-pointer whitespace-nowrap">
                    Enable GST/HST
                  </Label>
                </div>
              </FormItem>
            )}
          />
          <Button type="submit" className="whitespace-nowrap" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? "Saving…" : "Save Pricing"}
          </Button>
        </form>
      </Form>
    </div>
  );
}

// ─── Email Config Tab ─────────────────────────────────────────────────────────

const emailSchema = z.object({
  from_name: z.string().min(1, "Required"),
  from_email: z.string().email("Must be valid email"),
  email_provider: z.enum(["resend", "smtp"]),
  resend_api_key: z.string().optional(),
  smtp_host: z.string().optional(),
  smtp_port: z.coerce.number().optional(),
  smtp_username: z.string().optional(),
  smtp_password: z.string().optional(),
});

type EmailForm = z.infer<typeof emailSchema>;

function EmailConfigTab() {
  const { profile } = useAuth();
  const actorId = profile?.id ?? "";
  const qc = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: ["admin", "platform-settings"],
    queryFn: getPlatformSettings,
  });

  const settings = settingsQuery.data?.data ?? {};

  const form = useForm<EmailForm>({
    resolver: zodResolver(emailSchema),
    values: {
      from_name: settings.from_name ?? "Stratakey",
      from_email: settings.from_email ?? "noreply@stratakey.ca",
      email_provider: settings.email_provider ?? "resend",
      resend_api_key: settings.resend_api_key ?? "",
      smtp_host: settings.smtp_host ?? "",
      smtp_port: settings.smtp_port ?? 587,
      smtp_username: settings.smtp_username ?? "",
      smtp_password: settings.smtp_password ?? "",
    },
  });

  const provider = form.watch("email_provider");

  const saveMutation = useMutation({
    mutationFn: (data: EmailForm) => updatePlatformSettings(data, actorId),
    onSuccess: () => {
      toast.success("Email configuration saved");
      qc.invalidateQueries({ queryKey: ["admin", "platform-settings"] });
    },
    onError: () => toast.error("Failed to save"),
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => saveMutation.mutate(data))}
        className="space-y-5 max-w-lg"
      >
        <FormField
          control={form.control}
          name="from_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="whitespace-nowrap">From name</FormLabel>
              <FormControl><Input {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="from_email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="whitespace-nowrap">From email</FormLabel>
              <FormControl><Input type="email" {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email_provider"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="whitespace-nowrap">Email provider</FormLabel>
              <FormControl>
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="flex gap-4 mt-1"
                >
                  {(["resend", "smtp"] as const).map((v) => (
                    <div key={v} className="flex items-center gap-2">
                      <RadioGroupItem value={v} id={`provider-${v}`} />
                      <Label htmlFor={`provider-${v}`} className="text-sm font-normal capitalize cursor-pointer">{v === "resend" ? "Resend" : "SMTP"}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {provider === "resend" && (
          <FormField
            control={form.control}
            name="resend_api_key"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="whitespace-nowrap">Resend API key</FormLabel>
                <FormControl>
                  <Input type="password" placeholder="re_…" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {provider === "smtp" && (
          <div className="space-y-4 rounded-md border border-gray-200 p-4 bg-gray-50">
            <FormField
              control={form.control}
              name="smtp_host"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="whitespace-nowrap">SMTP host</FormLabel>
                  <FormControl><Input placeholder="smtp.example.com" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="smtp_port"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="whitespace-nowrap">Port</FormLabel>
                  <FormControl><Input type="number" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="smtp_username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="whitespace-nowrap">Username</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="smtp_password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="whitespace-nowrap">Password</FormLabel>
                  <FormControl><Input type="password" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button type="submit" className="whitespace-nowrap" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? "Saving…" : "Save Email Config"}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="whitespace-nowrap"
            onClick={() => {
              toast.success(`Test email sent to ${profile?.email ?? "your email"}`);
            }}
          >
            Send Test Email
          </Button>
        </div>
      </form>
    </Form>
  );
}

// ─── Feature Flags Tab ────────────────────────────────────────────────────────

const FLAG_DEFINITIONS: { key: string; label: string; description: string }[] = [
  { key: "guest_portal_enabled", label: "Guest Portal", description: "Guest portal accessible without login" },
  { key: "maintenance_requests_enabled", label: "Maintenance Requests", description: "Maintenance request tracking" },
  { key: "dispute_tracking_enabled", label: "Dispute Tracking", description: "Dispute resolution module" },
  { key: "income_tracker_enabled", label: "Income Tracker", description: "Income & expense tracking (Individual plan)" },
  { key: "cross_building_reports_enabled", label: "Cross-Building Reports", description: "Cross-building reports (PM plan)" },
  { key: "agm_export_enabled", label: "AGM Export", description: "AGM report export" },
  { key: "new_registrations_open", label: "New Registrations", description: "Allow new user registrations" },
  { key: "maintenance_mode", label: "Maintenance Mode", description: "Show maintenance page to non-admin users" },
];

function FeatureFlagsTab() {
  const { profile } = useAuth();
  const actorId = profile?.id ?? "";
  const qc = useQueryClient();

  const flagsQuery = useQuery({
    queryKey: ["admin", "feature-flags"],
    queryFn: getFeatureFlags,
  });

  const flagsData: Record<string, unknown>[] = Array.isArray(flagsQuery.data?.data)
    ? flagsQuery.data!.data
    : [];

  const getFlagValue = (key: string): boolean => {
    const match = flagsData.find((f) => (f.flag_name as string) === key);
    return (match?.enabled as boolean) ?? false;
  };

  const getFlagUpdated = (key: string): string | null => {
    const match = flagsData.find((f) => (f.flag_name as string) === key);
    return (match?.updated_at as string) ?? null;
  };

  const toggleMutation = useMutation({
    mutationFn: ({ flag, value }: { flag: string; value: boolean }) =>
      updateFeatureFlag(flag, value, actorId),
    onSuccess: (_, vars) => {
      toast.success(`${vars.flag} ${vars.value ? "enabled" : "disabled"}`);
      qc.invalidateQueries({ queryKey: ["admin", "feature-flags"] });
    },
    onError: () => toast.error("Failed to update flag"),
  });

  return (
    <div className="space-y-3 max-w-2xl">
      {flagsQuery.isLoading ? (
        Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-5 w-10 rounded-full" />
          </div>
        ))
      ) : (
        FLAG_DEFINITIONS.map((def) => {
          const enabled = getFlagValue(def.key);
          const updated = getFlagUpdated(def.key);
          return (
            <div
              key={def.key}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex-1 min-w-0 mr-4">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900 whitespace-nowrap">{def.label}</p>
                  {enabled ? (
                    <Badge className="text-[10px] bg-green-100 text-green-700 border-green-200 whitespace-nowrap">On</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] text-gray-400 whitespace-nowrap">Off</Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{def.description}</p>
                {updated && (
                  <p className="text-[10px] text-gray-300 mt-0.5">
                    Last changed: {new Date(updated).toLocaleDateString("en-CA")}
                  </p>
                )}
              </div>
              <Switch
                checked={enabled}
                onCheckedChange={(v) => toggleMutation.mutate({ flag: def.key, value: v })}
                disabled={toggleMutation.isPending}
                aria-label={`Toggle ${def.label}`}
              />
            </div>
          );
        })
      )}
    </div>
  );
}

// ─── Admin Users Tab ──────────────────────────────────────────────────────────

function AdminUsersTab() {
  const { profile } = useAuth();
  const actorId = profile?.id ?? "";
  const qc = useQueryClient();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("admin");
  const [revokeTarget, setRevokeTarget] = useState<Record<string, unknown> | null>(null);

  const adminsQuery = useQuery({
    queryKey: ["admin", "admin-users"],
    queryFn: getAdminUsers,
  });

  const admins: Record<string, unknown>[] = adminsQuery.data?.data ?? [];

  const inviteMutation = useMutation({
    mutationFn: () => inviteAdmin(inviteEmail, inviteRole, actorId),
    onSuccess: () => {
      toast.success(`Invitation sent to ${inviteEmail}`);
      qc.invalidateQueries({ queryKey: ["admin", "admin-users"] });
      setInviteOpen(false);
      setInviteEmail("");
    },
    onError: () => toast.error("Failed to send invitation"),
  });

  const revokeMutation = useMutation({
    mutationFn: (userId: string) => revokeAdmin(userId, actorId),
    onSuccess: () => {
      toast.success("Admin access revoked");
      qc.invalidateQueries({ queryKey: ["admin", "admin-users"] });
      setRevokeTarget(null);
    },
    onError: () => toast.error("Failed to revoke access"),
  });

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{admins.length} admin user{admins.length !== 1 ? "s" : ""}</p>
        <Button size="sm" onClick={() => setInviteOpen(true)} className="gap-2 whitespace-nowrap">
          <Users className="h-4 w-4" />
          Invite Admin
        </Button>
      </div>

      {adminsQuery.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      ) : admins.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-400">No admin users found</div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Role</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Added</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Last Active</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {admins.map((admin) => {
                const isSelf = (admin.id as string) === actorId;
                return (
                  <tr key={admin.id as string} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      <span className="truncate max-w-[160px] block">
                        {(admin.full_name as string) ?? "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <span className="truncate max-w-[180px] block">
                        {(admin.email as string) ?? "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="secondary" className="text-xs whitespace-nowrap">
                        {(admin.role as string) ?? "admin"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-gray-500 tabular-nums">
                      <span className="whitespace-nowrap">
                        {admin.created_at ? new Date(admin.created_at as string).toLocaleDateString("en-CA") : "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 tabular-nums">
                      <span className="whitespace-nowrap">
                        {admin.last_sign_in_at
                          ? new Date(admin.last_sign_in_at as string).toLocaleDateString("en-CA")
                          : "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50 whitespace-nowrap"
                                disabled={isSelf}
                                onClick={() => setRevokeTarget(admin)}
                              >
                                Revoke
                              </Button>
                            </span>
                          </TooltipTrigger>
                          {isSelf && (
                            <TooltipContent>
                              <p>Cannot revoke your own access</p>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Invite Admin</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="invite-email" className="whitespace-nowrap">Email address</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="admin@example.com"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="invite-role" className="whitespace-nowrap">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger id="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="system_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="whitespace-nowrap" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button
              className="whitespace-nowrap"
              onClick={() => inviteMutation.mutate()}
              disabled={inviteMutation.isPending || !inviteEmail}
            >
              {inviteMutation.isPending ? "Sending…" : "Send Invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Confirm */}
      <ConfirmModal
        open={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={() => { if (revokeTarget) revokeMutation.mutate(revokeTarget.id as string); }}
        title="Revoke Admin Access"
        description={`Remove admin access for ${(revokeTarget?.full_name as string) ?? (revokeTarget?.email as string) ?? "this user"}?`}
        loading={revokeMutation.isPending}
        variant="destructive"
      />
    </div>
  );
}

// ─── Danger Zone Tab ──────────────────────────────────────────────────────────

function DangerZoneTab() {
  const { profile } = useAuth();
  const actorId = profile?.id ?? "";

  const [flushOpen, setFlushOpen] = useState(false);
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);

  const flushMutation = useMutation({
    mutationFn: () => flushAllSessions(actorId),
    onSuccess: () => {
      toast.success("All sessions flushed — all users logged out");
      setFlushOpen(false);
    },
    onError: () => toast.error("Failed to flush sessions"),
  });

  const maintenanceMutation = useMutation({
    mutationFn: () => enableMaintenanceMode(actorId),
    onSuccess: () => {
      toast.success("Maintenance mode enabled");
      setMaintenanceOpen(false);
    },
    onError: () => toast.error("Failed to enable maintenance mode"),
  });

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="rounded-lg border-2 border-red-200 bg-red-50 p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-red-600" />
          <h3 className="font-semibold text-red-800 text-sm">Danger Zone</h3>
        </div>

        {/* Flush sessions */}
        <div className="flex items-start justify-between gap-4 pb-5 border-b border-red-200">
          <div>
            <p className="font-semibold text-sm text-red-900">Flush All Sessions</p>
            <p className="text-xs text-red-700 mt-0.5">
              Log out all users platform-wide immediately. Admins will need to log back in.
            </p>
          </div>
          <Button
            variant="destructive"
            size="sm"
            className="shrink-0 whitespace-nowrap"
            onClick={() => setFlushOpen(true)}
          >
            Flush Sessions
          </Button>
        </div>

        {/* Export DB dump */}
        <div className="flex items-start justify-between gap-4 pb-5 border-b border-red-200">
          <div>
            <p className="font-semibold text-sm text-red-900">Export Database Dump</p>
            <p className="text-xs text-red-700 mt-0.5">
              Trigger a secure full backup export. The download link will appear when ready.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 border-red-300 text-red-700 hover:bg-red-100 whitespace-nowrap"
            onClick={() => toast.info("Database export triggered — you'll receive a download link via email")}
          >
            Export Dump
          </Button>
        </div>

        {/* Maintenance mode */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-semibold text-sm text-red-900">Enable Maintenance Mode</p>
            <p className="text-xs text-red-700 mt-0.5">
              Show maintenance page to all non-admin users. Admins can still access the platform.
            </p>
          </div>
          <Button
            variant="destructive"
            size="sm"
            className="shrink-0 whitespace-nowrap"
            onClick={() => setMaintenanceOpen(true)}
          >
            Enable
          </Button>
        </div>
      </div>

      <ConfirmModal
        open={flushOpen}
        onClose={() => setFlushOpen(false)}
        onConfirm={() => flushMutation.mutate()}
        title="Flush All Sessions"
        description="This will immediately log out every user on the platform. All active sessions will be terminated."
        requireTyping
        loading={flushMutation.isPending}
        variant="destructive"
      />

      <ConfirmModal
        open={maintenanceOpen}
        onClose={() => setMaintenanceOpen(false)}
        onConfirm={() => maintenanceMutation.mutate()}
        title="Enable Maintenance Mode"
        description="Non-admin users will see a maintenance page until you disable this. Are you sure?"
        requireTyping
        loading={maintenanceMutation.isPending}
        variant="destructive"
      />
    </div>
  );
}

// ─── Main Settings Page ───────────────────────────────────────────────────────

export default function Settings() {
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Settings2 className="h-6 w-6 text-gray-600" />
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
      </div>

      <Tabs defaultValue="general">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="general" className="gap-1.5 whitespace-nowrap">
            <Settings2 className="h-3.5 w-3.5" />
            General
          </TabsTrigger>
          <TabsTrigger value="pricing" className="gap-1.5 whitespace-nowrap">
            <DollarSign className="h-3.5 w-3.5" />
            Pricing
          </TabsTrigger>
          <TabsTrigger value="email" className="gap-1.5 whitespace-nowrap">
            <Mail className="h-3.5 w-3.5" />
            Email Config
          </TabsTrigger>
          <TabsTrigger value="flags" className="gap-1.5 whitespace-nowrap">
            <Zap className="h-3.5 w-3.5" />
            Feature Flags
          </TabsTrigger>
          <TabsTrigger value="admins" className="gap-1.5 whitespace-nowrap">
            <Users className="h-3.5 w-3.5" />
            Admin Users
          </TabsTrigger>
          <TabsTrigger value="danger" className="gap-1.5 whitespace-nowrap text-red-600 data-[state=active]:text-red-700">
            <Trash2 className="h-3.5 w-3.5" />
            Danger Zone
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="general"><GeneralTab /></TabsContent>
          <TabsContent value="pricing"><PricingTab /></TabsContent>
          <TabsContent value="email"><EmailConfigTab /></TabsContent>
          <TabsContent value="flags"><FeatureFlagsTab /></TabsContent>
          <TabsContent value="admins"><AdminUsersTab /></TabsContent>
          <TabsContent value="danger"><DangerZoneTab /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
